import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

type ConfirmOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

type ConfirmApi = {
  confirm: (options: ConfirmOptions | string) => Promise<boolean>;
};

const ConfirmContext = createContext<ConfirmApi | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions>({ message: '' });
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions | string) => {
    const normalized =
      typeof options === 'string' ? { message: options } : options;
    setOpts(normalized);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (value: boolean) => {
    setOpen(false);
    resolver.current?.(value);
    resolver.current = null;
  };

  const api = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={api}>
      {children}
      {open ? (
        <div className="confirm-backdrop" role="presentation">
          <div
            className="confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            aria-describedby="confirm-desc"
          >
            <h3 id="confirm-title">{opts.title || 'Confirmar'}</h3>
            <p id="confirm-desc">{opts.message}</p>
            <div className="confirm-actions">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => close(false)}
              >
                {opts.cancelLabel || 'Cancelar'}
              </button>
              <button
                type="button"
                className={opts.danger ? 'btn-danger-outline' : 'btn-primary'}
                onClick={() => close(true)}
                autoFocus
              >
                {opts.confirmLabel || 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmApi {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    return {
      confirm: async (options) => {
        const message =
          typeof options === 'string' ? options : options.message;
        return window.confirm(message);
      },
    };
  }
  return ctx;
}
